import { z } from 'zod';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { ModelName } from '@casops/store/api';
import ModelForm from '@casops/components/layout/model-form/ModelForm';
import useModelForm from '@casops/components/layout/model-form/useModelForm';
import {
  AssetGroupFormModel,
  AssetGroupModel,
  CreateAssetGroupModel,
  EditAssetGroupModel,
  useCreateAssetGroupMutation,
  useEditAssetGroupMutation,
} from '@casops/store/apis/assetGroupsApi';
import { ValidNameRegex } from '@casops/store/apis/commonModels';
import { ServerErrorFields } from '@dmc/ui';

type DataModel = AssetGroupModel;
type FormModel = AssetGroupFormModel;
type CreateModel = CreateAssetGroupModel;
type EditModel = EditAssetGroupModel;

const modelName = ModelName.AssetGroup;

const DESCRIPTION_MAX = 255;

const defaultValues: FormModel = {
  name: '',
  desc: '',
};

const serverErrorFields: ServerErrorFields<FormModel> = {
  name: 'Name',
  desc: 'Description',
};

interface AssetGroupFormProps {
  item?: DataModel;
}

export const AssetGroupForm = ({ item }: AssetGroupFormProps): ReactElement => {
  const { t } = useTranslation();

  const schema = useMemo(() => {
    return z.object({
      name: z
        .string()
        .min(1, { message: t('Validation.SpecifyAssetGroupName') })
        .max(128, { message: t('Validation.AssetGroupNameTooLong') })
        .regex(ValidNameRegex, { message: t('Validation.InvalidCharacters') }),
      desc: z.string().max(DESCRIPTION_MAX, {
        message: t('Validation.DescriptionTooLong', { max: DESCRIPTION_MAX }),
      }),
    });
  }, [t]);

  const [triggerCreateItem] = useCreateAssetGroupMutation();
  const [triggerEditItem] = useEditAssetGroupMutation();

  const { canSubmit, onSubmit, errorMessage, control } = useModelForm<
    DataModel,
    FormModel,
    CreateModel,
    EditModel
  >({
    modelName,
    defaultValues,
    item,
    schema,
    triggerCreateItem,
    triggerEditItem,
    serverErrorFields,
  });

  return (
    <ModelForm
      canSubmit={canSubmit}
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      control={control}
    >
      <>
        <CasFormInput key="name" control={control} name="name" label={t('Fields.AssetGroupName')} />
        <CasFormInput
          key={'desc'}
          control={control}
          name="desc"
          label={t('Fields.Description')}
          element="textarea"
        />
      </>
    </ModelForm>
  );
};

export default AssetGroupForm;
